// Historial de textos libres que el usuario repite entre generaciones
// (ADR-2026-08-12-historial-instrucciones-reusables).
//
// Vive sobre `historial_prompts`, la tabla que ya usaba la interfaz vieja:
//   id_company | prompt | tipo | created_at
//
// El `tipo` dice de qué campo salió el texto, y hay una asimetría importante:
//   • 'escena'      → fondo personalizado. Lo escribe el BACKEND
//     (atlas-backend/src/services/ads/postEffects.ts) cuando la generación
//     manda `background_mode === 'descripcion'`, que es exactamente lo que
//     manda la v2. Acá SOLO se lee.
//   • 'instruccion' → "Instrucción adicional". No cae en ninguna condición del
//     backend, así que lo escribimos desde el front: así queda cubierto también
//     lo que todavía se genera por n8n, que el backend nunca vería.
//   • 'creativo'    → prompt del modo creativo viejo. No se toca.
//   • 'video'       → el prompt del modo "Creador de video"
//     (ADR-2026-09-08-creador-de-video-recuerda-sus-prompts). Como
//     'instruccion', lo escribe el FRONT: el backend no tiene una condición que
//     lo cubra. Tipo propio y no 'instruccion': son textos de otra naturaleza
//     —una escena en movimiento, no un ajuste sobre una foto— y mezclarlos haría
//     que cada modo ofreciera los textos del otro.
//   • 'prompt_libre' → el prompt del modo "Prompt libre"
//     (ADR-2026-09-11-prompt-libre-recuerda-sus-prompts). Igual que 'video': lo
//     escribe el FRONT y lleva tipo propio. Lo que se escribe acá es la
//     descripción COMPLETA de una imagen, no un ajuste sobre una foto ya armada
//     ('instruccion') ni una escena en movimiento ('video').
//
// `tipo` es `text` libre (sin check ni enum), así que sumar un valor no necesita
// migración: verificado contra la base el 2026-09-08.
//
// La tabla guarda UNA FILA POR GENERACIÓN, así que viene naturalmente con
// repetidos: la deduplicación es en lectura (mismo patrón que
// `EscenaPersonalizadaOption`), sin índices ni constraints nuevos.
import { supabase } from '../../lib/supabase';

export type PromptHistoryTipo =
  | 'instruccion'
  | 'escena'
  | 'video'
  | 'prompt_libre';

/** Se piden de más para poder deduplicar y aun así llenar la lista. */
const FETCH_MULTIPLIER = 3;

export async function fetchPromptHistory(
  companyId: string | null | undefined,
  tipo: PromptHistoryTipo,
  limit = 10,
): Promise<string[]> {
  if (!companyId) return [];
  try {
    const { data, error } = await supabase
      .from('historial_prompts')
      .select('prompt')
      .eq('id_company', companyId)
      .eq('tipo', tipo)
      .order('created_at', { ascending: false })
      .limit(limit * FETCH_MULTIPLIER);
    if (error || !data) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const row of data as { prompt: string | null }[]) {
      const prompt = (row.prompt || '').trim();
      if (!prompt || seen.has(prompt)) continue;
      seen.add(prompt);
      out.push(prompt);
      if (out.length >= limit) break;
    }
    return out;
  } catch {
    // Best-effort: sin historial, el botón simplemente no aparece.
    return [];
  }
}

// Cache por (empresa, tipo) compartida entre todas las instancias del botón:
// los textareas de instrucción adicional están montados de a varios (mobile +
// desktop en el mismo árbol), y sin esto cada uno dispararía su propia query.
const cache = new Map<string, Promise<string[]>>();

const cacheKey = (companyId: string, tipo: PromptHistoryTipo) =>
  `${companyId}:${tipo}`;

/** Igual que `fetchPromptHistory` pero memoizado: una sola query por tipo. */
export function getPromptHistory(
  companyId: string | null | undefined,
  tipo: PromptHistoryTipo,
  limit = 10,
): Promise<string[]> {
  if (!companyId) return Promise.resolve([]);
  const key = cacheKey(companyId, tipo);
  let pending = cache.get(key);
  if (!pending) {
    pending = fetchPromptHistory(companyId, tipo, limit);
    cache.set(key, pending);
  }
  return pending;
}

// Avisar a los botones montados que la cache cambió. Sin esto, la PRIMERA
// instrucción de una empresa no aparecería hasta recargar la app: el botón se
// esconde cuando no hay historial, así que nadie volvería a pedirlo.
type Listener = () => void;
const listeners = new Set<Listener>();

export function subscribePromptHistory(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function invalidatePromptHistory(
  companyId: string | null | undefined,
  tipo: PromptHistoryTipo,
): void {
  if (!companyId) return;
  cache.delete(cacheKey(companyId, tipo));
  listeners.forEach((fn) => fn());
}

/**
 * Guarda un texto usado. Fire-and-forget A PROPÓSITO: se llama en el camino de
 * generar, y el historial nunca puede demorar ni romper una generación — por eso
 * no se devuelve la promesa ni se propaga el error.
 */
export function recordPromptHistory(
  companyId: string | null | undefined,
  tipo: PromptHistoryTipo,
  prompt: string | null | undefined,
): void {
  const texto = (prompt || '').trim();
  if (!companyId || !texto) return;
  void Promise.resolve(
    supabase
      .from('historial_prompts')
      .insert({ id_company: companyId, prompt: texto, tipo }),
  ).then(
    ({ error }) => {
      if (error) {
        console.warn('[promptHistory] insert falló', error);
        return;
      }
      // Que la próxima apertura del menú vea lo recién usado.
      invalidatePromptHistory(companyId, tipo);
    },
    (err) => console.warn('[promptHistory] insert falló', err),
  );
}
